<script lang="ts" setup>
import { ULink } from '#components'

const props = defineProps({
  attributes: {
    default: () => [],
    type: Array as PropType<ApiAttribute[]>,
  },
  description: {
    default: () => '',
    type: [String, Array] as PropType<string | string[]>,
  },
  endpoint: {
    default: '',
    type: String,
  },
  example: {
    default: undefined,
    type: [
      Array,
      Boolean,
      Number,
      Object,
      String,
    ],
  },
  hasDivider: {
    default: true,
    type: Boolean,
  },
  hasSegmentation: {
    default: false,
    type: Boolean,
  },
  level: {
    default: 1,
    type: Number,
  },
  link: {
    default: undefined,
    type: Object as PropType<ApiAttributeLink>,
  },
  maxLength: {
    default: 0,
    type: Number,
  },
  name: {
    required: true,
    type: String,
  },
  optional: {
    default: false,
    type: Boolean,
  },
  parent: {
    default: '',
    type: String,
  },
  required: {
    default: false,
    type: Boolean,
  },
  type: {
    required: true,
    type: String as PropType<ApiAttributeType>,
  },
  values: {
    default: () => [],
    type: Array as PropType<ApiAttributeValue[]>,
  },
  warning: {
    default: '',
    type: String,
  },
})

const { createCondition } = useSegmentation()
const { t } = useI18n()

const expanded = ref(false)

const query = ref<SegmentationCondition[]>([
  createCondition({
    attribute: 'about.industries',
    blockedOperator: false,
    operator: 'or',
    sign: 'equals',
    values: [
      'retail',
      'internet',
      'services',
    ],
  }),

  createCondition({
    attribute: 'locations.headquarters.country.code',
    blockedOperator: true,
    operator: 'or',
    sign: 'equals',
    values: [
      'us',
      'fr',
      'gb',
    ],
  }),
])

const formattedValues = computed((): UBadge[] => {
  return props.values.map((value) => {
    return typeof value === 'object' ? value : { label: value }
  })
})

function onToggleAttributes(event: MouseEvent) {
  if (event && event.stopPropagation) {
    event.stopPropagation()
  }

  expanded.value = !expanded.value
}
</script>

<template>
  <div class="relative flex flex-col gap-3 text-base leading-normal">
    <div
      class="flex items-center"
      :class="{ 'xl:pr-36': attributes?.length && level <= 2 }"
    >
      <span
        v-if="parent"
        class="font-normal text-gray-700 dark:text-gray-300"
      >
        {{ parent }}.
      </span>

      <span class="text-gray-900 dark:text-white">
        {{ name }}
      </span>

      <span class="ml-1.5 flex gap-1.5 text-xs lowercase">
        <span
          :class="{
            'text-sky-600 dark:text-sky-400': type === 'array',
            'text-violet-600 dark:text-violet-400': type === 'boolean',
            'text-emerald-600 dark:text-emerald-400': type === 'date',
            'text-amber-600 dark:text-amber-400': type === 'number',
            'text-fuchsia-600 dark:text-fuchsia-400': type === 'object',
            'text-cyan-600 dark:text-cyan-400': type === 'string',
          }"
        >
          {{ type }}
        </span>

        <span
          v-if="maxLength"
          class="text-slate-700 dark:text-slate-300"
        >
          ({{ t("maxLength", { count: maxLength }) }})
        </span>

        <span
          v-if="required"
          class="text-red-700 dark:text-red-300"
        >
          ({{ t("required") }})
        </span>

        <span
          v-else-if="optional"
          class="text-slate-700 dark:text-slate-300"
        >
          ({{ t("optional") }})
        </span>
      </span>
    </div>

    <template v-if="!Array.isArray(description)">
      <p
        class="-mt-1.5 break-words text-gray-900 dark:text-white"
        :class="{ 'xl:pr-36': attributes?.length && level <= 2 }"
      >
        {{ description.toLowerCase().startsWith('api') ? t(description) : description }}
      </p>
    </template>

    <template v-else>
      <p
        v-for="(line, lineIndex) in description"
        :key="lineIndex"
        class="-mt-1.5 break-words text-gray-900 dark:text-white"
      >
        {{ line.toLowerCase().startsWith('api') ? t(line) : line }}
      </p>
    </template>

    <p
      v-if="example !== undefined"
      class="-mt-1.5 flex items-start overflow-hidden truncate text-gray-600 dark:text-gray-400"
    >
      <UIcon
        class="size-4.5 mr-1 mt-1 flex-shrink-0"
        :name="getIcon('arrowPointingRight')"
      />

      <span class="overflow-x-auto">
        {{ Array.isArray(example) ? JSON.stringify(example) : example }}
      </span>
    </p>

    <p
      v-if="warning"
      class="flex items-center"
    >
      <UIcon
        class="mr-2 size-5 flex-shrink-0 text-orange-700 dark:text-orange-300"
        :name="getIcon('warning')"
      />

      {{ warning }}
    </p>

    <template v-if="values && values.length">
      <p class="mt-1 text-xs uppercase text-gray-500 dark:text-gray-300">
        {{ t('possibleValues') }}
      </p>

      <BaseBadges
        :badges="formattedValues"
        class="-mt-0.5"
        :max="10"
        :uppercase="false"
        variant="subtle"
      />
    </template>

    <Component
      :is="link?.to ? ULink : 'div'"
      v-if="link"
      class="flex items-center hover:underline"
      :target="link?.to ? '_blank' : undefined"
      :to="link?.to"
    >
      <UIcon
        class="size-4.5 mr-2 flex-shrink-0"
        :class="{
          'text-indigo-700 dark:text-indigo-300': typeof link?.to === 'string',
          'text-purple-700 dark:text-purple-300': typeof link?.to === 'object',
        }"
        :name="getIcon(typeof link?.to === 'object' ? 'datapoints' : 'download')"
      />

      {{ t(link?.label) }}
    </Component>

    <UButton
      v-if="attributes?.length"
      class="self-start"
      :class="{ 'xl:absolute xl:right-0 xl:top-0': level <= 2 }"
      :icon="expanded ? 'clarity:eye-hide-line' : 'tabler:text-plus'"
      :label="expanded ? t('hideAttributes') : t('showAttributes')"
      size="xs"
      @click="onToggleAttributes"
    />

    <div
      v-if="expanded"
      class="mt-1 flex flex-col gap-5 rounded-lg border border-gray-300 p-5 shadow-sm dark:border-gray-700"
      :class="{
        'bg-gray-200 bg-opacity-50 dark:bg-gray-800 dark:bg-opacity-50': level === 1,
        'bg-gray-300 bg-opacity-20 dark:bg-gray-700 dark:bg-opacity-20': level === 2,
        'bg-gray-400 bg-opacity-10 dark:bg-gray-600 dark:bg-opacity-10': level === 3,
        'bg-gray-500 bg-opacity-5 dark:bg-gray-500 dark:bg-opacity-5': level === 4,
      }"
    >
      <ApiAttribute
        v-for="(attribute, attributeIndex) in attributes"
        :key="attribute.name"
        :attributes="attribute.attributes"
        class="overflow-x-auto"
        :description="attribute.description"
        :endpoint="attribute.endpoint"
        :example="attribute.example"
        :has-divider="attributeIndex !== attributes.length - 1"
        :has-segmentation="attribute.hasSegmentation"
        :level="level + 1"
        :link="attribute.link"
        :name="attribute.name"
        :optional="attribute.optional"
        :parent="attribute.parent"
        :required="attribute.required"
        :type="attribute.type"
        :values="attribute.values"
        :warning="attribute.warning"
      />
    </div>

    <SegmentationVertical
      v-if="hasSegmentation && !expanded"
      v-model="query"
      class="mt-1"
      :endpoint="endpoint"
      feature="search"
      has-borders
      has-query-preview
    />

    <UDivider
      v-if="hasDivider"
      class="mt-3"
      :ui="{ border: { base: 'border-dashed border-gray-400 dark:border-gray-600' } }"
    />
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "hideAttributes": "Hide attributes",
    "maxLength": "Max length: {count}",
    "optional": "optional",
    "possibleValues": "Possible values",
    "required": "required",
    "showAttributes": "Show attributes"
  },
  "es": {
    "hideAttributes": "Ocultar atributos",
    "maxLength": "Longitud máxima: {count}",
    "optional": "opcional",
    "possibleValues": "Valores posibles",
    "required": "requerido",
    "showAttributes": "Mostrar atributos"
  },
  "fr": {
    "hideAttributes": "Masquer attributs",
    "maxLength": "Longueur max: {count}",
    "optional": "optionnel",
    "possibleValues": "Valeurs possibles",
    "required": "requis",
    "showAttributes": "Afficher attributs"
  }
}
</i18n>
